import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Wand2, Coins, Building2, Gamepad2, Briefcase, Lock, LogOut, X, Zap,
  ArrowUpRight, RefreshCw, Loader2, Bot, ShieldQuestion, Layers, Receipt, BarChart3,
} from "lucide-react";
import { useSession } from "@/context/SessionContext";
import { socialApi } from "@/api/social";
import { vaultSecurityApi } from "@/api/vaultSecurity";
import { USER_ID } from "@/constants/hubs";
import TopDeck, { DECK_OPTIONS } from "@/components/dashboard/TopDeck";
import MistSuite from "@/components/mist/MistSuite";
import { BusinessTools, WealthMonitor } from "@/components/dashboard/HubEngines";
import HubDetail from "@/components/dashboard/HubDetail";
import CareerDesk from "@/components/dashboard/CareerDesk";
import BlueprintStudio from "@/components/dashboard/BlueprintStudio";
import BusinessDesk from "@/components/dashboard/BusinessDesk";
import { VaultSecuritySuite } from "@/components/dashboard/VaultSecurity";
import VaultGate from "@/components/dashboard/VaultGate";
import VaultRepository from "@/components/dashboard/VaultRepository";
import PersonalDataHub from "@/components/hub/PersonalDataHub";
import JimTerminal from "@/components/dashboard/JimTerminal";
import AdminPortal from "@/components/dashboard/AdminPortal";
import MisteckSuperAppReel from "@/components/dashboard/MisteckSuperAppReel";
import TaxBooksPage from "@/components/tax/TaxBooksPage";
import CommandStackS6 from "@/components/dashboard/CommandStackS6";
import SiteStatsPage from "@/components/dashboard/SiteStatsPage";
import MistSixPillarHome from "@/components/dashboard/MistSixPillarHome";
import S20ScarcityBanner from "@/components/dashboard/S20ScarcityBanner";
import AustracSandboxBadge from "@/components/dashboard/AustracSandboxBadge";
import { billingApi } from "@/api/billing";
import DesktopEscape from "@/components/shell/DesktopEscape";

const ORANGE = "#F2994A";

// Command Center macro grid — Image-1 palette conservation + premium framing.
const CONTAINERS = [
  { hubKey: "imagery_hub", title: "Make a Video & Design", color: "#E2D4F8", Icon: Wand2 },
  { hubKey: "vault", title: "My Secure Documents", color: "#111111", Icon: Lock, locked: true },
  { hubKey: "business_hub", title: "My Expenses & Taxes", color: "#A4DCFA", Icon: Building2 },
  { hubKey: "wealth_hub", title: "Wealth & Money", color: "#98FFB6", Icon: Coins },
  { hubKey: "career_hub", title: "Career", color: "#FFD700", Icon: Briefcase },
  { hubKey: "lifestyle_hub", title: "Leisure", color: "#FF9E9E", Icon: Gamepad2 },
  { hubKey: "blueprint_design", title: "Design Studio", color: "#D4AF37", Icon: Layers },
  { hubKey: "tax_books", title: "Receipts & Bookkeeping", color: "#C9A227", Icon: Receipt },
  { hubKey: "site_stats", title: "Site Stats", color: "#00E5C0", Icon: BarChart3 },
];
function greetingFor(d = new Date()) {
  const h = d.getHours();
  if (h < 12) return "Good Morning";
  if (h < 18) return "Good Afternoon";
  return "Good Evening";
}

/* --------------- Net Worth matrix + live keyless Solana SCRAPE ------------- */
function NetWorthMatrix({ onOpenWealth }) {
  const [canvas, setCanvas] = useState(null);
  const [addr, setAddr] = useState("");
  const [openScrape, setOpenScrape] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [live, setLive] = useState(null);

  useEffect(() => { vaultSecurityApi.networthCanvas().then(setCanvas).catch(() => {}); }, []);

  const scrape = async () => {
    if (addr.trim().length < 32) { toast.error("Paste a public Solana address"); return; }
    setScraping(true);
    try {
      const s = await vaultSecurityApi.web3Scan(addr.trim());
      setLive(s);
      toast.success(`Live · $${(s.total_usd || 0).toLocaleString()} · ${s.token_count} coins`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Scrape failed"); }
    finally { setScraping(false); }
  };

  const total = live ? live.total_usd : (canvas?.summary?.net_worth ?? 0);
  const series = (canvas?.series || []).slice(-14).map((p) => Number(p.v) || 0);
  const max = Math.max(...series, 1), min = Math.min(...series, 0);
  const span = max - min || 1;
  const pts = series.length > 1
    ? series.map((v, i) => `${(i / (series.length - 1)) * 100},${28 - ((v - min) / span) * 24}`).join(" ")
    : "0,16 100,16";

  return (
    <div className="mt-4 border-4 border-charcoal rounded-[24px] bg-white p-4 shadow-[4px_4px_0px_#1A1A1A]" data-testid="networth-matrix">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[11px] font-black uppercase tracking-[0.22em] text-charcoal">Net Worth</p>
        <button onClick={onOpenWealth} data-testid="networth-wealth-link" className="flex items-center gap-0.5 font-mono text-[10px] font-black uppercase tracking-widest" style={{ color: ORANGE }}>Wealth <ArrowUpRight size={12} /></button>
      </div>
      <p className="mt-1.5 font-serif text-4xl text-charcoal tabular-nums" data-testid="networth-total">${Math.round(total).toLocaleString()}</p>
      <p className="font-mono text-[8.5px] uppercase tracking-[0.18em] text-charcoal/45 mt-0.5">Crypto + Shares + Cash · 30-Day Trend</p>

      <svg viewBox="0 0 100 28" preserveAspectRatio="none" className="w-full h-9 mt-3" data-testid="networth-trend">
        <polyline points={pts} fill="none" stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={pts.split(" ").slice(-1)[0].split(",")[0]} cy={pts.split(" ").slice(-1)[0].split(",")[1]} r="2.4" fill={ORANGE} stroke="#1A1A1A" strokeWidth="0.8" />
      </svg>

      {live && (
        <div className="mt-2 flex items-center justify-between font-mono text-[9px] border-2 border-charcoal/15 rounded-lg px-2 py-1.5 bg-cream" data-testid="networth-live">
          <span className="text-charcoal/70">◎ {live.sol.balance} SOL @ ${live.sol.price_usd}</span>
          <span className="text-charcoal font-black">{live.token_count} coins · ${live.total_usd.toLocaleString()}</span>
        </div>
      )}
      {live && live.total_usd === 0 && (
        <p className="mt-1 font-mono text-[8px] text-charcoal/40 leading-snug" data-testid="networth-live-note">Chain read OK — these holdings have no active market price (dust / delisted).</p>
      )}

      {openScrape && (
        <input value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="Phantom / Solana public address"
          data-testid="networth-scrape-input"
          className="mt-2 w-full bg-cream border-2 border-charcoal rounded-lg px-2.5 py-2 font-mono text-[10px] text-charcoal outline-none focus:ring-2 focus:ring-gold" />
      )}
      <button
        onClick={() => (openScrape ? scrape() : setOpenScrape(true))}
        disabled={scraping}
        data-testid="networth-scrape-btn"
        className="mt-2 w-full flex items-center justify-center gap-1.5 bg-gold text-black border-2 border-charcoal font-mono text-[10px] font-black uppercase tracking-[0.18em] py-2.5 rounded-full shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
        {scraping ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
        {scraping ? "Scraping chain…" : openScrape ? "Scrape Live Balance" : "Scrape On-Chain (Solana)"}
      </button>
    </div>
  );
}

/* -------------------------------- dashboard ------------------------------ */
export default function MyDashboard({ onNavVisibility, onNavigate }) {
  const { user, setUser, logout } = useSession();
  const [open, setOpen] = useState(() => {
    // Pull-to-refresh + Quick-Action recovery: re-open the right hub from the URL.
    const params = new URLSearchParams(window.location.search);
    if (params.get("studio")) return CONTAINERS.find((c) => c.hubKey === "imagery_hub") || null;
    const h = params.get("hub");
    if (h) return CONTAINERS.find((c) => c.hubKey === h) || null;
    return null;
  });
  const [vaultUnlocked, setVaultUnlocked] = useState(false);
  const [secSuite, setSecSuite] = useState(false);
  // bizDeal renders the LTD conversion deck (BusinessTools) — reached ONLY via the
  // Secure Vault upgrade CTA. The business_hub TILE now opens BusinessDesk (tax receiver).
  const [bizDeal, setBizDeal] = useState(false);
  const [isJimOpen, setIsJimOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const jimAllowed = ["djohnm1972@gmail.com"].includes((user?.email || "").toLowerCase())
    || ["johnnywalker"].includes((user?.handle || "").toLowerCase())
    || ["founder", "admin"].includes(user?.role);
  const [deckPicker, setDeckPicker] = useState(false);
  const [savingDeck, setSavingDeck] = useState(false);
  const [lifetimeOffer, setLifetimeOffer] = useState(null);

  // Stage 5 — pull the per-user Lifetime Pass offer state (hidden for founder/admin).
  useEffect(() => {
    if (jimAllowed) return;
    billingApi.lifetimeOffer().then(setLifetimeOffer).catch(() => {});
  }, [jimAllowed]);

  const dismissLifetime = async () => {
    try { await billingApi.lifetimeDismiss(); } catch { /* silent */ }
  };

  const lastScroll = useRef(0);
  const deckMode = user?.top_deck_mode || "reels";
  const handle = (user?.handle || user?.user_id || USER_ID || "creator").toUpperCase();

  useEffect(() => {
    onNavVisibility?.(!!open || bizDeal || adminOpen);
    return () => onNavVisibility?.(false);
  }, [open, bizDeal, adminOpen, onNavVisibility]);

  // Re-sync the open hub with the URL on browser back/forward AND when the
  // universal AppHeader back-pill clears the `hub` / `studio` params.
  useEffect(() => {
    const syncFromUrl = () => {
      const p = new URLSearchParams(window.location.search);
      if (p.get("studio")) { setOpen(CONTAINERS.find((c) => c.hubKey === "imagery_hub") || null); return; }
      const h = p.get("hub");
      setOpen(h ? (CONTAINERS.find((c) => c.hubKey === h) || null) : null);
    };
    window.addEventListener("popstate", syncFromUrl);
    return () => window.removeEventListener("popstate", syncFromUrl);
  }, []);

  // DESKTOP dual-pane: this dashboard is the persistent 1/3 chassis navigator.
  // When a top-level nav zone is switched (or DECK reset), clear any deep view it
  // has portalled into the 2/3 pane. Fired desktop-only by AppShell → mobile is
  // never affected.
  useEffect(() => {
    const onDeskNav = () => {
      setOpen(null); setBizDeal(false); setIsJimOpen(false); setAdminOpen(false);
      setVaultUnlocked(false); setSecSuite(false);
    };
    window.addEventListener("mist:desk-nav", onDeskNav);
    return () => window.removeEventListener("mist:desk-nav", onDeskNav);
  }, []);


  const onScroll = (e) => {
    const y = e.currentTarget.scrollTop;
    if (y > lastScroll.current + 6 && y > 40) onNavVisibility?.(true);
    else if (y < lastScroll.current - 6) onNavVisibility?.(false);
    lastScroll.current = y;
  };

  const closeLanding = () => {
    setOpen(null); setVaultUnlocked(false); setSecSuite(false);
    const p = new URLSearchParams(window.location.search);
    if (p.has("studio") || p.has("view") || p.has("hub")) {
      p.delete("studio"); p.delete("view"); p.delete("hub");
      const qs = p.toString();
      window.history.replaceState(window.history.state, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
      // If a Misteck Life zone launched this desk (?zone=life retained), re-open
      // it so back/collapse restores the Life context instead of the root.
      if (p.has("zone")) window.dispatchEvent(new PopStateEvent("popstate"));
    }
  };
  const reconfigVault = () => { setUser((u) => ({ ...u, has_pin: false })); setVaultUnlocked(false); setSecSuite(false); };

  // Open a hub AND record it in the URL history so the universal back-pill shows
  // and browser/hardware BACK naturally collapses the hub one level (never exits
  // mid-flow). The locked Vault stays URL-less (its own gateway owns that flow).
  const openHub = (hubKey) => {
    const c = CONTAINERS.find((x) => x.hubKey === hubKey);
    if (!c) return;
    setOpen(c);
    if (c.locked) return; // Secure Vault: gateway-driven, no deep-link.
    const p = new URLSearchParams(window.location.search);
    p.set("tab", "command"); p.set("hub", hubKey); p.delete("studio"); p.delete("view");
    window.history.pushState({ hub: hubKey }, "", `${window.location.pathname}?${p}`);
  };
  const openWealth = () => openHub("wealth_hub");

  const pickDeck = async (mode) => {
    setSavingDeck(true);
    try {
      const { user: updated } = await socialApi.updateProfile({ top_deck_mode: mode });
      setUser((prev) => ({ ...prev, ...updated }));
      setDeckPicker(false);
    } catch { toast.error("Could not update deck"); }
    finally { setSavingDeck(false); }
  };

  return (
    <div className="h-full w-full flex flex-col bg-slate-950 relative overflow-hidden" data-testid="my-dashboard">
      {/* Sub-view routing — engines attached behind each macro tile onClick.
          DESKTOP: each heavy view is lifted into the 2/3 workspace pane via
          <DesktopEscape>. MOBILE: renders inline in the chassis (100% unchanged).
          The imagery hub (MistSuite) owns its own desktop docking → left as-is. */}
      {open && open.hubKey === "imagery_hub" && <MistSuite onBack={closeLanding} />}
      {open && open.hubKey === "site_stats" && <DesktopEscape z="z-[54]"><SiteStatsPage onBack={closeLanding} isFounder={jimAllowed} /></DesktopEscape>}
      {open && open.hubKey === "career_hub" && <DesktopEscape z="z-[54]"><CareerDesk onBack={closeLanding} /></DesktopEscape>}
      {open && open.hubKey === "blueprint_design" && <DesktopEscape z="z-[54]"><BlueprintStudio onBack={closeLanding} /></DesktopEscape>}
      {open && open.hubKey === "business_hub" && <DesktopEscape z="z-[54]"><BusinessDesk onBack={closeLanding} /></DesktopEscape>}
      {open && open.hubKey === "wealth_hub" && <DesktopEscape z="z-[54]"><WealthMonitor onBack={closeLanding} onOpenTax={() => openHub("tax_books")} /></DesktopEscape>}
      {open && open.hubKey === "tax_books" && <DesktopEscape z="z-[54]"><TaxBooksPage onBack={closeLanding} /></DesktopEscape>}
      {open && open.hubKey === "lifestyle_hub" && (
        <DesktopEscape z="z-[54]"><HubDetail hub={open} onBack={closeLanding} /></DesktopEscape>
      )}
      {bizDeal && <DesktopEscape z="z-[54]"><BusinessTools onBack={() => setBizDeal(false)} /></DesktopEscape>}
      {isJimOpen && <DesktopEscape z="z-[56]"><JimTerminal onClose={() => setIsJimOpen(false)} /></DesktopEscape>}
      {adminOpen && <DesktopEscape z="z-[56]"><AdminPortal onBack={() => setAdminOpen(false)} /></DesktopEscape>}
      {open && open.locked && (
        <DesktopEscape z="z-[54]">
        <VaultGate
          user={user}
          vaultMode={user?.vault_mode || "pin"}
          unlocked={vaultUnlocked}
          onUnlock={() => setVaultUnlocked(true)}
          onOnboarded={() => { setUser((u) => ({ ...u, has_pin: true, vault_mode: "pin" })); setVaultUnlocked(true); }}
          onRelock={() => setVaultUnlocked(false)}
          onBack={closeLanding}
        >
          <PersonalDataHub onBack={closeLanding} />
          {secSuite && <VaultSecuritySuite onBack={() => setSecSuite(false)} />}
        </VaultGate>
        </DesktopEscape>
      )}

      {/* Deck-swap picker */}
      {deckPicker && (
        <div className="absolute inset-0 z-40 bg-black/70 flex items-end" data-testid="deck-picker">
          <div className="w-full bg-cream border-t-4 border-charcoal rounded-t-3xl p-5">
            <div className="flex items-center gap-2 pb-3 border-b-2 border-charcoal/10 mb-3">
              <h3 className="font-serif text-xl text-charcoal flex-1">Top Monitor Deck</h3>
              <button onClick={() => setDeckPicker(false)} data-testid="deck-picker-close" className="w-8 h-8 grid place-items-center rounded-lg border-2 border-charcoal bg-white shadow-[1.5px_1.5px_0px_#1A1A1A]"><X size={15} /></button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {DECK_OPTIONS.map((d) => {
                const active = deckMode === d.mode;
                return (
                  <button key={d.mode} onClick={() => pickDeck(d.mode)} disabled={savingDeck} data-testid={`deck-opt-${d.mode}`}
                    className={["flex flex-col items-center justify-center gap-1 rounded-xl border-2 py-2.5 transition-colors", active ? "bg-charcoal text-gold border-gold" : "bg-white text-charcoal/60 border-charcoal/30"].join(" ")}>
                    <d.Icon size={16} strokeWidth={2.3} />
                    <span className="font-mono text-[8px] font-black uppercase tracking-[0.1em]">{d.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pt-5 pb-8" onScroll={onScroll}>
        {/* AUSTRAC ComplianceGuard — Founder Preview Sandbox banner (top of panel) */}
        <AustracSandboxBadge isFounder={jimAllowed} />
        {/* STAGE 5 — Early-Adopter Lifetime Pass scarcity banner (top of panel) */}
        {!jimAllowed && lifetimeOffer && (
          <div className="-mx-4 -mt-5 mb-4" data-testid="scarcity-banner-wrap">
            <S20ScarcityBanner passData={lifetimeOffer} onDismissBanner={dismissLifetime} />
          </div>
        )}
        {/* TOP-THIRD GREETING — Image 2 white header playground */}
        <div data-testid="dashboard-greeting">
          <p className="font-mono text-[11px] font-black uppercase tracking-[0.32em]" style={{ color: ORANGE }} data-testid="greeting-line">{greetingFor().toUpperCase()}</p>
          <h1 className="mt-1 font-sans font-black text-white leading-[0.95] tracking-tight text-4xl" data-testid="greeting-handle">
            {handle}<span style={{ color: ORANGE }}>.</span>
          </h1>
          <p className="mt-2 font-mono text-[11px] leading-relaxed text-white/50">Your Command Center — launch studios, track jobs, and watch your worth move.</p>
        </div>

        {/* STAGE 6 · PHASE 1 — 3-Tile OLED Command Stack (thumb-zone vertical launcher) */}
        <div className="mt-5" data-testid="command-stack-wrap">
          <CommandStackS6
            user={user}
            onRouteHub={(hubKey) => openHub(hubKey)}
            onNavigate={(v) => onNavigate?.(v)}
          />
        </div>

        {/* 6-PILLAR MATRIX HOME — flat structural launcher over the existing hubs */}
        <div className="mt-5" data-testid="six-pillar-wrap">
          <MistSixPillarHome
            isFounder={jimAllowed}
            onOpenHub={(hubKey) => openHub(hubKey)}
            onNavigate={(v) => onNavigate?.(v)}
          />
        </div>

        {/* PHASE 31 — Unified Super App Reel (admin slot-config for founders) */}
        <div className="mt-5" data-testid="mist-intro-reel-wrap">
          <MisteckSuperAppReel isAdmin={jimAllowed} />
        </div>

        {/* LATEST REEL FRAME — outer white card (Image 2) wrapping the dark reel core (Image 1) */}
        <div className="mt-5 border-4 border-charcoal rounded-[24px] bg-white p-3 shadow-[4px_4px_0px_#1A1A1A]" data-testid="latest-reel-frame">
          <div className="flex items-center justify-between mb-2 px-1">
            <p className="font-mono text-[11px] font-black uppercase tracking-[0.22em] text-charcoal">Latest Reel</p>
            <button onClick={() => onNavigate?.("reels")} data-testid="latest-reel-library" className="flex items-center gap-0.5 font-mono text-[10px] font-black uppercase tracking-widest" style={{ color: ORANGE }}>Library <ArrowUpRight size={12} /></button>
          </div>
          <TopDeck mode={deckMode} onNavigate={onNavigate} onSettings={() => setDeckPicker(true)} />
        </div>

        {/* ACTIVE IDENTITY BANNER — deep-black tracking panel, pulsating yellow LIVE */}
        <div className="mt-4 border-4 border-charcoal rounded-3xl bg-charcoal px-4 py-3 shadow-[5px_5px_0px_#D4AF37]" data-testid="dashboard-identity">
          <div className="flex items-center justify-between">
            <div className="leading-none">
              <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-gold">Active Identity</p>
              <p className="font-mono text-base font-bold text-white" data-testid="dashboard-identity-name">{user?.user_id || USER_ID}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1 font-mono text-[9px] font-black uppercase tracking-widest text-gold animate-pulse" data-testid="identity-live-badge"><Zap size={12} className="text-gold" /> live</span>
              <button onClick={logout} data-testid="dashboard-logout-btn" aria-label="Log out" className="w-8 h-8 rounded-lg bg-white/10 border-2 border-gold/60 grid place-items-center active:scale-95">
                <LogOut size={14} className="text-gold" />
              </button>
            </div>
          </div>
        </div>

        {/* SITE STATS — public "what's live" usability board (Play + Life) */}
        <button onClick={() => openHub("site_stats")} data-testid="site-stats-launch-btn"
          className="mt-3 w-full flex items-center justify-between border-4 border-charcoal rounded-[24px] bg-charcoal px-4 py-3.5 shadow-[4px_4px_0px_#00E5C0] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-transform">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl border-2 border-[#00E5C0] grid place-items-center bg-white/5"><BarChart3 size={20} strokeWidth={2.2} className="text-[#00E5C0]" /></span>
            <div className="text-left leading-tight">
              <p className="font-serif text-lg text-white">Site Stats</p>
              <p className="font-mono text-[8px] uppercase tracking-[0.18em] text-[#00E5C0]/80">Usability · Misteck Play &amp; Life</p>
            </div>
          </div>
          <ArrowUpRight size={18} className="text-[#00E5C0]" />
        </button>

        {/* JIM — system companion terminal launcher (founder/admin gated) */}
        {jimAllowed && (
        <button onClick={() => setIsJimOpen(true)} data-testid="jim-launch-btn"
          className="mt-3 w-full flex items-center justify-between border-4 border-charcoal rounded-[24px] bg-charcoal px-4 py-3.5 shadow-[4px_4px_0px_#38BDF8] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-transform">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl border-2 border-[#38BDF8] grid place-items-center bg-white/5"><Bot size={20} strokeWidth={2.2} className="text-[#38BDF8]" /></span>
            <div className="text-left leading-tight">
              <p className="font-serif text-lg text-white">JIM</p>
              <p className="font-mono text-[8px] uppercase tracking-[0.18em] text-[#38BDF8]/80">System Companion</p>
            </div>
          </div>
          <ArrowUpRight size={18} className="text-[#38BDF8]" />
        </button>
        )}

        {/* ADMIN PANEL — founder-only admin launcher (Business workspace) */}
        {jimAllowed && (
        <button onClick={() => setAdminOpen(true)} data-testid="command-center-launch-btn"
          className="mt-3 w-full flex items-center justify-between border-4 border-gold rounded-[24px] bg-charcoal px-4 py-3.5 shadow-[4px_4px_0px_#D4AF37] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-transform">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl border-2 border-gold grid place-items-center bg-white/5"><ShieldQuestion size={20} strokeWidth={2.2} className="text-gold" /></span>
            <div className="text-left leading-tight">
              <p className="font-serif text-lg text-white">Admin Panel</p>
              <p className="font-mono text-[8px] uppercase tracking-[0.18em] text-gold/80">Payouts · Engine · Config · Tools</p>
            </div>
          </div>
          <ArrowUpRight size={18} className="text-gold" />
        </button>
        )}

        {/* BASE ENGINE — Net Worth matrix + keyless Solana SCRAPE (Component 5) */}
        <NetWorthMatrix onOpenWealth={openWealth} />
      </div>
    </div>
  );
}
